import { AttachedFile } from "@/src/api/Tenant";

/**
 * Sekcje plików do pobrania na stronie publicznej procedury.
 * Struktura przechowywana w procedure.payload.downloadSections.
 *
 * Każda sekcja ma tytuł (np. "Plan Ogólny - GML") i listę ID plików
 * które do niej należą. Pliki są uploadowane jak dotąd (POST /.../file)
 * — grupowanie dzieje się wyłącznie w payload metadanych procedury.
 *
 * Pliki bez przypisanej sekcji (np. wgrane zanim admin stworzył sekcję)
 * wyświetlane są w fallback'owej sekcji "Pozostałe pliki".
 */

export type DownloadSection = {
  /** Tytuł sekcji widoczny mieszkańcom. */
  title: string;
  /** ID plików przypisanych do tej sekcji (odniesienie do procedure.files[n].id). */
  fileIds: string[];
};

/** Predefiniowane nazwy sekcji — admin może wybrać z listy albo wpisać własne. */
export const PREDEFINED_SECTION_TITLES: string[] = [
  "Plan Ogólny - GML",
  "Plan Ogólny - Uzasadnienie",
  "Plan Ogólny - Uzasadnienie Mapa Uwarunkowań",
  "Prognoza Oddziaływania na środowisko",
  "Projekt MPZP",
  "GeoTIF dla MPZP",
];

/**
 * Grupuje pliki procedury wg downloadSections. Pliki nieprzypisane trafiają
 * do wirtualnej sekcji "Pozostałe pliki" tylko jeżeli istnieją.
 */
export function groupFilesBySections(
  files: AttachedFile[] | undefined,
  sections: DownloadSection[] | undefined,
): Array<{ title: string; files: AttachedFile[] }> {
  const allFiles = files ?? [];
  const definedSections = sections ?? [];

  const assignedIds = new Set<string>();
  const groups: Array<{ title: string; files: AttachedFile[] }> = [];

  for (const section of definedSections) {
    const sectionFiles: AttachedFile[] = [];
    for (const fileId of section.fileIds) {
      const file = allFiles.find((f) => f.id === fileId);
      if (file) {
        sectionFiles.push(file);
        assignedIds.add(fileId);
      }
    }
    if (sectionFiles.length > 0) {
      groups.push({ title: section.title, files: sectionFiles });
    }
  }

  // Pliki nie przypisane do żadnej sekcji — fallback
  const unassigned = allFiles.filter((f) => !assignedIds.has(f.id));
  if (unassigned.length > 0) {
    groups.push({ title: "Pozostałe pliki", files: unassigned });
  }

  return groups;
}
